import React from "react";
import AppContext from "@/js/common/AppContext";
import Button from "@/js/common/ui/Button";
import GridTable, { fldInfsProps } from "@/js/common/ui/GridTable";
import AdminGridStyle from "@/js/common/ui/style/AdminGridStyle";
import Util from "@/js/common/Util";
import TextBox from "@/js/common/ui/input/TextBox";
import SelectBox from "@/js/common/ui/input/SelectBox";
import DatePickerBox from "@/js/common/ui/input/DatePickerBox";
import NumberTextBox from "@/js/common/ui/input/NumberTextBox";
import Axios from "@/js/common/Axios";
import * as Atom from "@/js/common/Atom";
import { useRecoilValue } from "recoil";
import BudgetFile from "../budgetFileBox/BudgetFile";
import { budgetFilesProps } from "@/js/common/types/types";
import GrnamtNoticeBox, { GrnamtPrefill } from "./dialog/GrnamtNoticeBox";
import AtrzLineBox from "../bgtovrl/dialog/AtrzLineBox";

const BgtMbsdsDpstMng = () => {
  const userInfo = useRecoilValue(Atom.userInfo);

  const [upOgnzCd, setUpOgnzCd] = React.useState<string>(userInfo.upOgnzCd); // 상위조직코드
  const [ognzCd, setOgnzCd] = React.useState<string>(userInfo.ognzCd); // 조직코드(지부)
  const [acntgYear, setAcntgYear] = React.useState<string>(Util.getNowAcntgYear); // 회계연도
  const [searchAtrzSttsCd, setSearchAtrzSttsCd] = React.useState<string>(""); // 진행상태코드

  const [callTime, setCallTime] = React.useState<Date>(new Date()); // 검색 트리거

  const [mbsdsDpstDocId, setMbsdsDpstDocId] = React.useState<string>(""); // 수입문서ID(PK)
  const [dpstRsdocNo, setDpstRsdocNo] = React.useState<string>(""); // 결의서 문서번호
  const [dpstDt, setDpstDt] = React.useState<string>(""); // 수입일자(YYYY-MM-DD)
  const [kwanCd, setKwanCd] = React.useState<string>(""); // 관 코드(bgtSbjct1stCd)
  const [hangCd, setHangCd] = React.useState<string>(""); // 항 코드(bgtSbjct2stCd)
  const [mokCd, setMokCd] = React.useState<string>(""); // 목 코드(bgtSbjct3stCd)
  const [summary, setSummary] = React.useState<string>(""); // 적요(abstrCn)
  const [memo, setMemo] = React.useState<string>(""); // 메모(memo)
  const [amount, setAmount] = React.useState<string>(""); // 수입금액(dpstAmt)
  const [atrzSttsCd, setAtrzSttsCd] = React.useState<string>("00"); // 상태 정보(작성중,작성완료)
  const [rejectRsn, setRejectRsn] = React.useState<string>(""); // 반려사유(atrzOpnnCn)
  const [sumDpstAmt, setSumDpstAmt] = React.useState<string>("0");
  const [listLine, setListLine] = React.useState<any[]>([]);  // 결재 라인 정보
  const [openLine, setOpenLine] = React.useState<boolean>(false); // 기본 펼침/접힘
  const [dataArr, setDataArr] = React.useState<any[]>([]); // 목록(list)
  const [budgetFiles, setBudgetFiles] = React.useState<File[]>([]); // 저장할 파일
  const [savedBudgetFiles, setSavedBudgetFiles] = React.useState<budgetFilesProps[]>([]); // 저장된 파일 리스트

  // =========================
  // helpers
  // =========================
  const alertMsg = (msg: string) => {
    AppContext.openAlert({
      title: "알림",
      msg,
      icon: "info",
      closeText: "확인",
    });
  };

  const confirmBox = (msg: string, onConfirm: () => void) => {
    AppContext.openAlert({
      title: "확인",
      msg,
      icon: "question",
      closeText: "취소",
      confirmText: "확인",
      handleConfirm: onConfirm,
    });
  };

  const clearForm = () => {
    setMbsdsDpstDocId("");
    setDpstRsdocNo("");
    setDpstDt("");
    setKwanCd("");
    setHangCd("");
    setMokCd("");
    setSummary("");
    setMemo("");
    setAmount("");
    setAtrzSttsCd("00");
    setRejectRsn("");
    setBudgetFiles([]);
    setSavedBudgetFiles([]);
  };

  // 문서 ID 변경 시 드롭존 초기화
  React.useEffect(() => {
      setBudgetFiles([]);
  }, [mbsdsDpstDocId]);

  // =========================
  // GridTable 컬럼
  // =========================
  const fldInfs: fldInfsProps[] = [
    { type: "date", fldNm: "dpstDt", width: 100, headAlign: "center", dataAlign: "center", label: "수입일자" },
    { type: "text", fldNm: "bgtSbjct3stNm", width: 100, headAlign: "center", dataAlign: "center", label: "목" },
    { type: "text", fldNm: "abstrCn", width: 160, headAlign: "center", dataAlign: "left", label: "적요" },
    { type: "comma",fldNm: "dpstAmt", width: 100, headAlign: "center", dataAlign: "right", label: "금액" },
    { type: "el", fldNm: "atrzSttsNm", width: 100, headAlign: "center", dataAlign: "center", label: "진행상태",
      el: (item: any) => {
          return (
              <span className={"ls06 " + (item.atrzSttsCd === "02" ? "fc007bff" : "")}>{item.atrzSttsNm}</span> // 작성완료 Text 색상 변경
          )
      }
    },
    { type: "text", fldNm: "crtDt", width: 130, headAlign: "center", dataAlign: "center", label: "작성일시" },
    { type: "text", fldNm: "dpstRsdocNo", width: 120, headAlign: "center", dataAlign: "center", label: "문서번호" },
  ];

  // =========================
  // 목록 조회
  // =========================
  React.useEffect(() => {
    AppContext.showSpinner();

    Axios.dataAccess({
      url: "system/bgt/mbsds/dpst/select-bgt-mbsds-dpst-list.do",
      methodType: "post",
      paramType: "json",
      paramData: {
        orgCd: ognzCd,
        acntgYear: acntgYear,
        searchAtrzSttsCd: searchAtrzSttsCd,
      },
      onSuccessFn: (res: any) => {
        setDataArr(res.result.list);
        const sum = res.result.listSum?.sumDpstAmt ?? 0;
        setSumDpstAmt(String(sum));

        AppContext.hideSpinner();
      },
    });
  }, [callTime, ognzCd, upOgnzCd, acntgYear, searchAtrzSttsCd]);

  // =========================
  // 상세 조회(행 클릭)
  // =========================
  const fetchDtl = (docId: string) => {
    if (!docId) return;

    AppContext.showSpinner();

    Axios.dataAccess({
      url: "system/bgt/mbsds/dpst/select-bgt-mbsds-dpst-dtl.do",
      methodType: "post",
      paramType: "json",
      paramData: {
        mbsdsDpstDocId: docId,
      },
      onSuccessFn: (res: any) => {
        const item = res.result.item;

        setMbsdsDpstDocId(item.mbsdsDpstDocId);
        setDpstRsdocNo(item.dpstRsdocNo);
        setDpstDt(item.dpstDt);
        setKwanCd(item.bgtSbjct1stCd);
        setHangCd(item.bgtSbjct2stCd);
        setMokCd(item.bgtSbjct3stCd);
        setSummary(item.abstrCn);
        setMemo(item.memo);
        setAmount(item.dpstAmt);
        setRejectRsn(item.atrzOpnnCn);
        setAtrzSttsCd(item.atrzSttsCd);
        setListLine(res.result.listLine);
        setSavedBudgetFiles(res.result.listFile);
        AppContext.hideSpinner();
      },
    });
  };

  // =========================
  //  교부금 하달 → 수입 입력폼 자동 세팅
  // =========================
  const onPickGrnamt = (prefill: GrnamtPrefill) => {
    // 새 작성이므로 문서ID/파일/반려사유는 초기화하는게 자연스러움
    setMbsdsDpstDocId("");
    setAtrzSttsCd("00");
    setRejectRsn("");
    setBudgetFiles([]);
    setSavedBudgetFiles([]);

    setDpstDt(prefill.dpstDt);
    setKwanCd(prefill.kwanCd);
    setHangCd(prefill.hangCd);
    setMokCd(prefill.mokCd);
    setAmount(prefill.amount);
    setSummary(prefill.summary);
    setMemo(prefill.memo);

  };

  // =========================
  // 저장
  // =========================
  const validateForSave = (mode: "temp" | "done") => {
    if (!ognzCd) return alertMsg("지부를 선택해주세요."), false;

    if (!dpstDt) return alertMsg("수입일자를 입력/선택해주세요."), false;

    if (!kwanCd || !hangCd || !mokCd) return alertMsg("관/항/목을 모두 선택해주세요."), false;

    if (!summary) return alertMsg("적요를 입력해주세요."), false;

    if (mode === "done") {
      const amt = Number(Util.uncomma(String(amount)).trim()) || 0;
      if (amt <= 0) return alertMsg("금액을 입력해주세요."), false;
    }

    return true;
  };

  const doSave = (mode: "temp" | "done") => {
    if (!validateForSave(mode)) return;

    const stts = mode === "temp" ? "01" : "02";

    const payload: any = {
      mbsdsDpstDocId: mbsdsDpstDocId || null,
      orgCd: ognzCd,
      acntgYear: Util.getAcntgYear(dpstDt),
      dpstDt: dpstDt,
      bgtSbjct1stCd: kwanCd,
      bgtSbjct2stCd: hangCd,
      bgtSbjct3stCd: mokCd,
      dpstAmt: Number(Util.uncomma(String(amount)).trim()),
      abstrCn: summary,
      memo: memo,
      atrzSttsCd: stts,
      budgetFiles: budgetFiles, // 저장 파일
      fileSeCd: "A01", // 파일구분코드(조합비_수입내역[A01])
    };

    const msg = mode === "temp" ? "임시저장 하시겠습니까?" : "작성완료 처리하시겠습니까?";

    confirmBox(msg, () => {
      AppContext.showSpinner();

      Axios.dataAccess({
        url: "system/bgt/mbsds/dpst/save-bgt-mbsds-dpst.do",
        methodType: "post",
        isMultipart: true,
        paramData: payload,
        onSuccessFn: (res: any) => {
          AppContext.hideSpinner();
          alertMsg("저장되었습니다.");
          if (Util.isEmpty(mbsdsDpstDocId)) {
              setMbsdsDpstDocId(res.result.mbsdsDpstDocId);
          }
          if (stts === "01") {
              setAtrzSttsCd("01");
          } else if(stts === "02") {
              clearForm(); // 초기화
          }
          setCallTime(new Date());
        },
      });
    });
  };

  // =========================
  // 삭제(선택된 상세에서만)
  // =========================
  const doDelete = () => {
    if (!mbsdsDpstDocId) {
      alertMsg("삭제할 문서를 선택(클릭)해주세요.");
      return;
    }

    confirmBox("선택한 수입내역을 삭제하시겠습니까?", () => {
      AppContext.showSpinner();

      Axios.dataAccess({
        url: "system/bgt/mbsds/dpst/delete-bgt-mbsds-dpst.do",
        methodType: "post",
        paramType: "json",
        paramData: {
          mbsdsDpstDocId,
        },
        onSuccessFn: () => {
          AppContext.hideSpinner();
          alertMsg("삭제되었습니다.");
          clearForm();
          setCallTime(new Date());
        },
      });
    });
  };

  return (
    <div className="w1200 bgcWhite p20 bdrd8 mt10 boxShadowSoft">
      {/* 타이틀 */}
      <div className="mt5">
        <span className="dpBlock w100p fm-c fs25 fc2c3e50 ls1 pb10">운영비 수입결의서 관리</span>
        <div className="bdt3 bdc007bff pb10" />
      </div>

      {/* 검색 조건 */}
      <div className="bd1 bdcDDDDDD p20 mb10 bdrd6 bgcFAFAFA">
        <div className="dpFlx aiC flxWrap gap14">
          {/* 본부 */}
          <div className="dpFlx aiC">
            <span className="w50 fm-c fs15 fc333333 ls1 taL">본부</span>
            <SelectBox
              value={upOgnzCd}
              width={150}
              height={36}
              fontSize={14}
              fontFamily="fm-c"
              color="#333"
              letterSpacing={-1}
              borderColor="#ddd"
              borderOnColor="#222"
              borderRadius={2}
              background="#f6f6f6"
              codeArray={Util.getUpOrgCodeArray("01")}
              placeholder="전체"
              paddingRight={8}
              disabled={true}
              onChangeFunc={(data: string) => setUpOgnzCd(data)}
            />
          </div>

          {/* 지부 */}
          <div className="dpFlx aiC">
            <span className="w50 fm-c fs15 fc333333 ls1 taL">지부</span>
            <SelectBox
              value={ognzCd}
              width={180}
              height={36}
              fontSize={14}
              fontFamily="fm-c"
              color="#333"
              letterSpacing={-1}
              borderColor="#ddd"
              borderOnColor="#222"
              borderRadius={2}
              background="#f6f6f6"
              codeArray={Util.getDownOrgCodeArray(upOgnzCd)}
              placeholder="전체"
              paddingRight={8}
              disabled={true}
              onChangeFunc={(data: string) => setOgnzCd(data)}
            />
          </div>

          {/* 회계연도 */}
          <div className="dpFlx aiC">
            <span className="w80 fm-c fs15 fc333333 ls1 taL">회계연도</span>
            <SelectBox
              value={acntgYear}
              width={120}
              height={36}
              fontSize={14}
              fontFamily="fm-c"
              color="#333"
              letterSpacing={-1}
              borderColor="#ddd"
              borderOnColor="#222"
              borderRadius={2}
              background="#fff"
              codeArray={Util.getAcntgYearArray()}
              placeholder="선택"
              paddingRight={8}
              onChangeFunc={(data: string) => setAcntgYear(data)}
            />
          </div>

          {/* 진행상태 */}
          <div className="dpFlx aiC">
            <span className="w80 fm-c fs15 fc333333 ls1 taL">진행상태</span>
            <SelectBox
              value={searchAtrzSttsCd}
              width={140}
              height={36}
              fontSize={14}
              fontFamily="fm-c"
              color="#333"
              letterSpacing={-1}
              borderColor="#ddd"
              borderOnColor="#222"
              borderRadius={2}
              background="#fff"
              codeArray={[{ code: "", name: "전체" }].concat(Util.getCodeArray("UI_ATRZ_STTS_CD_01"))}
              placeholder="전체"
              paddingRight={8}
              onChangeFunc={(data: string) => setSearchAtrzSttsCd(data)}
            />
          </div>
        </div>
      </div>

      {/* 교부금 하달 알림 */}
      <GrnamtNoticeBox acntgYear={acntgYear} ognzCd={ognzCd} onPick={onPickGrnamt} />

      {/* 좌/우 */}
      <div className="dpFlx gap14 aiFs">
        {/* LEFT */}
        <div className="w360">
          <div className="bd1d9d9d9 bdrd6 bgcWhite">
            <div className="p15">
              <div className="mb10 dpFlx aiC">
                <span className="fm-b fs16 fc007bff ls1 fw700">수입결의서 입력</span>
                <span className="fm-b fs13 ls06 fc1A5276" style={{ marginLeft: "auto", whiteSpace: "nowrap" }}>
                  {dpstRsdocNo}
                </span>
              </div>
              {atrzSttsCd === "05" && (
              <AtrzLineBox
                listLine={listLine}
                open={openLine}
                onToggle={() => setOpenLine((v) => !v)}
              />)}

              {/* 수입일자 */}
              <div className="dpFlx flxdCol mb10">
                <div className="h30 dpFlx aiE pb5">
                  <span className="fm-b fs13 ls06 fc111111">수입일자</span>
                </div>
                <DatePickerBox
                  width={320}
                  height={36}
                  fontSize={14}
                  fontFamily="fm-c"
                  color="#333"
                  letterSpacing={-1}
                  borderColor="#ddd"
                  borderOnColor="#222"
                  borderRadius={2}
                  bgColor="white"
                  textAlign="left"
                  calendarThemeColor={AppContext.themeColor}
                  className=""
                  value={dpstDt}
                  onChange={(data: string) => setDpstDt(data)}
                />
              </div>

              {/* 관/항/목 */}
              <div className="dpFlx mb10">
                <div className="dpFlx flxdCol flx1">
                  <div className="h30 dpFlx aiE pb5">
                    <span className="fm-b fs13 ls06 fc111111">관</span>
                  </div>
                  <SelectBox
                    value={kwanCd}
                    width={100}
                    height={36}
                    fontSize={14}
                    fontFamily="fm-c"
                    color="#333"
                    letterSpacing={-1}
                    borderColor="#ddd"
                    borderOnColor="#222"
                    borderRadius={2}
                    background="#fff"
                    codeArray={Util.getBgt1CodeArray("01", ognzCd)}
                    placeholder="선택"
                    paddingRight={8}
                    onChangeFunc={(v: string) => {
                      setKwanCd(v);
                      setHangCd("");
                      setMokCd("");
                    }}
                  />
                </div>

                <div className="dpFlx flxdCol flx1">
                  <div className="h30 dpFlx aiE pb5">
                    <span className="fm-b fs13 ls06 fc111111">항</span>
                  </div>
                  <SelectBox
                    value={hangCd}
                    width={100}
                    height={36}
                    fontSize={14}
                    fontFamily="fm-c"
                    color="#333"
                    letterSpacing={-1}
                    borderColor="#ddd"
                    borderOnColor="#222"
                    borderRadius={2}
                    background="#fff"
                    codeArray={Util.getBgt2CodeArray(kwanCd, ognzCd)}
                    placeholder="선택"
                    paddingRight={8}
                    onChangeFunc={(v: string) => {
                      setHangCd(v);
                      setMokCd("");
                    }}
                  />
                </div>

                <div className="dpFlx flxdCol flx1">
                  <div className="h30 dpFlx aiE pb5">
                    <span className="fm-b fs13 ls06 fc111111">목</span>
                  </div>
                  <SelectBox
                    value={mokCd}
                    width={100}
                    height={36}
                    fontSize={14}
                    fontFamily="fm-c"
                    color="#333"
                    letterSpacing={-1}
                    borderColor="#ddd"
                    borderOnColor="#222"
                    borderRadius={2}
                    background="#fff"
                    codeArray={Util.getBgt3CodeArray(hangCd, ognzCd)}
                    placeholder="선택"
                    paddingRight={8}
                    onChangeFunc={(v: string) => setMokCd(v)}
                  />
                </div>
              </div>

              {/* 적요 */}
              <div className="dpFlx flxdCol mb10">
                <div className="h30 dpFlx aiE pb5">
                  <span className="fm-b fs13 ls06 fc111111">적요</span>
                </div>
                <TextBox
                  value={summary}
                  width={320}
                  height={36}
                  fontSize={14}
                  fontFamily="fm-c"
                  color="#333"
                  letterSpacing={-1}
                  borderColor="#ddd"
                  borderOnColor="#222"
                  borderRadius={2}
                  placeholder="수입 관련 적요를 입력하세요"
                  style={{ backgroundColor: "#fff", padding: "0 12px" }}
                  onChangeFunc={(t: string) => setSummary(t)}
                />
              </div>

              {/* 메모 */}
              <div className="dpFlx flxdCol mb10">
                <div className="h30 dpFlx aiE pb5">
                  <span className="fm-b fs13 ls06 fc111111">메모</span>
                </div>
                <TextBox
                  value={memo}
                  width={320}
                  height={36}
                  fontSize={14}
                  fontFamily="fm-c"
                  color="#333"
                  letterSpacing={-1}
                  borderColor="#ddd"
                  borderOnColor="#222"
                  borderRadius={2}
                  placeholder="수입 관련 메모를 입력하세요"
                  style={{ backgroundColor: "#fff", padding: "0 12px" }}
                  onChangeFunc={(t: string) => setMemo(t)}
                />
              </div>

              {/* 금액 */}
              <div className="dpFlx flxdCol mb10">
                <div className="h30 dpFlx aiE pb5">
                  <span className="fm-b fs13 ls06 fc111111">금액</span>
                </div>
                <div className="w320 h36 bgcWhite dpFlx aiC bsBdb">
                  <NumberTextBox
                    value={amount}
                    onChange={(v: string) => setAmount(v)}
                    className="w320 h36 bd1 bdrd2"
                    thousandsGroupStyle="thousand"
                    thousandSeparator=","
                    placeholder="0"
                    fontSize={14}
                    fontFamily="fm-c"
                    inputClass="taR mr10"
                    letterSpacing={-1}
                  />
                </div>
              </div>

              {/* 조합비 첨부파일 */}
              <BudgetFile
                atrzSttsCd={searchAtrzSttsCd}
                fileSeCd="A01"
                savedBudgetFiles={savedBudgetFiles}
                setSavedBudgetFiles={setSavedBudgetFiles}
                budgetFiles={budgetFiles}
                setBudgetFiles={setBudgetFiles}
              ></BudgetFile>

              {/* 반려 */}
              {atrzSttsCd === "05" && (
                <div className="dpFlx flxdCol">
                  <div className="h30 dpFlx aiE pb5">
                    <span className="fm-b fs13 ls06 fc111111">반려</span>
                  </div>
                  <TextBox
                    value={rejectRsn}
                    width={320}
                    height={36}
                    fontSize={14}
                    fontFamily="fm-c"
                    color="#333"
                    letterSpacing={-1}
                    borderColor="#ddd"
                    borderOnColor="#222"
                    borderRadius={2}
                    style={{ backgroundColor: "#fff", padding: "0 12px" }}
                    mode="readonly"
                    onChangeFunc={(text: string) => setRejectRsn(text)}
                  />
                </div>
              )}

              <div className="bdb1 bdcDDDDDD mt15 mb15" />

              {/* 버튼 */}
              <div className="dpFlx aiC jcSb gap10">
                  <Button buttonName="초기화"
                      width={80} height={40}
                      fontFamily="fm-b" fontSize={16} letterSpacing={-1}
                      variant="styInitBtn" borderRadius={6}
                      buttonClass="dpFlx aiC jcC"
                      onClickFunc={() => clearForm()}
                  />

                  {(atrzSttsCd === "01" || atrzSttsCd === "02" || atrzSttsCd === "05") &&
                      <Button buttonName="삭제"
                          width={75} height={40}
                          fontFamily="fm-b" fontSize={15} letterSpacing={-1}
                          variant="styDeleteBtn" borderRadius={6}
                          buttonClass="dpFlx aiC jcC"
                          onClickFunc={doDelete}
                      />
                  }

                  {(atrzSttsCd === "00" || atrzSttsCd === "01") &&
                      <Button buttonName="임시저장"
                          width={100} height={40}
                          fontFamily="fm-b" fontSize={16} letterSpacing={-1}
                          variant="styTempBtn" borderRadius={6}
                          buttonClass="dpFlx aiC jcC"
                          onClickFunc={() => doSave("temp")}
                      />
                  }

                  <Button buttonName="작성완료"
                      width={100} height={40}
                      fontFamily="fm-b" fontSize={16} letterSpacing={-1}
                      variant="styCompleteBtn" borderRadius={6}
                      buttonClass="dpFlx aiC jcC"
                      onClickFunc={() => doSave("done")}
                  />
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT */}
        <div className="flx1">
          <div className="bd1d9d9d9 bdrd6 bgcWhite">
            <div className="p5 pl15 pr15 pb15">
              <div className="dpFlx aiC jcSb mb10 h44">
                <div className="fm-b fs16 fc007bff ls1 fw700">수입결의서 목록</div>
              </div>

              {/* 리스트 */}
              <div className="w100p ofxA ofyH bdt1 bdcEBEBEB pt6">
                <div className="mw900">
                  <GridTable
                    tableInfo={{
                      tableHeadVsb: true,
                      styleType: AdminGridStyle,
                      fldInfs: fldInfs,
                      ordFld: { name: "dpstDt", direction: "desc" },
                      dataArr: dataArr,
                      maxRowNums: [100],
                      theadTrHeight: 40,
                      tbodyTrHeight: 35,
                      paginateClass: "pt10",
                      paginateStyle: {
                        buttonOnColor: AppContext.boardThemeColor,
                        buttonWidth: 22,
                        buttonHeight: 24,
                        buttonMarginLeft: 1,
                        buttonMarginRight: 1,
                        fontSize: 13,
                        color: "#666666",
                        letterSpacing: -0.8,
                        fontFamily: "Pretendard-Regular",
                        borderRadius: 3,
                      },
                      rowClickFunc: (item: any) => {
                        const docId = item.mbsdsDpstDocId;
                        if (docId) fetchDtl(String(docId));
                      },
                    }}
                  />
                </div>

                <table className="w100p bgcDCDCDC">
                  <colgroup>
                    <col style={{ width: "100px" }} />
                    <col style={{ width: "100px" }} />
                    <col style={{ width: "160px" }} />
                    <col style={{ width: "100px" }} />
                    <col style={{ width: "100px" }} />
                    <col style={{ width: "130px" }} />
                    <col style={{ width: "120px" }} />
                  </colgroup>
                  <tbody>
                    <tr style={{ height: "35px" }}>
                      <td></td>
                      <td></td>
                      <td style={{ textAlign: "center" }}>
                        <span className="fs15 ls06 fc2c3e50 fw500">합계</span>
                      </td>
                      <td>
                        <div className="taR pt5 pr5">
                          <span className="fs15 ls075">{Util.comma(sumDpstAmt)}</span>
                        </div>
                      </td>
                      <td></td>
                      <td></td>
                      <td></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BgtMbsdsDpstMng;
